import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Check, Info } from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = '/api';

// Komponent pomocniczy dla pojedynczej karty Rolki (aby obsłużyć stan hover niezależnie)
const ReelCard = ({ reel, index, copyToClipboard, copiedItems }) => {
  const [hoveredSegment, setHoveredSegment] = useState(null);

  const segments = [
    { id: 1, text: reel.script_segment_1, analysis: reel.analysis_point_1, label: 'Sytuacja', color: 'bg-slate-100', border: 'border-slate-400', text_color: 'text-slate-800', highlight: 'bg-slate-200' },
    { id: 2, text: reel.script_segment_2, analysis: reel.analysis_point_2, label: 'Punkt Bólu', color: 'bg-red-50', border: 'border-red-400', text_color: 'text-red-900', highlight: 'bg-red-100' },
    { id: 3, text: reel.script_segment_3, analysis: reel.analysis_point_3, label: 'Dowód', color: 'bg-orange-50', border: 'border-orange-400', text_color: 'text-orange-900', highlight: 'bg-orange-100' },
    { id: 4, text: reel.script_segment_4, analysis: reel.analysis_point_4, label: 'Fakt', color: 'bg-blue-50', border: 'border-blue-400', text_color: 'text-blue-900', highlight: 'bg-blue-100' },
    { id: 5, text: reel.script_segment_5, analysis: reel.analysis_point_5, label: 'Konsekwencje', color: 'bg-indigo-50', border: 'border-indigo-400', text_color: 'text-indigo-900', highlight: 'bg-indigo-100' },
    { id: 6, text: reel.script_segment_6, analysis: reel.analysis_point_6, label: 'Plan/Action', color: 'bg-emerald-50', border: 'border-emerald-400', text_color: 'text-emerald-900', highlight: 'bg-emerald-100' },
  ];

  const fullScript = segments.map(s => s.text).join('\n\n');

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden mb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 md:px-8 py-6 text-white">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <div className="flex flex-wrap gap-3 mb-3">
              <span className="px-3 py-1 bg-white/10 backdrop-blur text-white text-xs font-bold rounded-full uppercase tracking-wider border border-white/20">
                {reel.meta_template_name}
              </span>
              <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-lg shadow-indigo-500/30">
                {reel.meta_core_emotion}
              </span>
            </div>
            <h4 className="text-2xl font-bold leading-tight">{reel.script_title}</h4>
          </div>
          <button
            onClick={() => copyToClipboard(fullScript, `reel-full-${index}`)}
            className="px-5 py-2.5 bg-white text-slate-900 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center gap-2 shadow-lg flex-shrink-0 self-start"
          >
            {copiedItems[`reel-full-${index}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Kopiuj całość
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
        {/* Left Column: Continuous Script */}
        <div className="p-6 md:p-8 lg:col-span-7 bg-white">
          <h5 className="font-bold text-gray-900 mb-6 flex items-center gap-2 uppercase tracking-wide text-sm">
            🎬 Scenariusz
          </h5>
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap font-medium">
            {segments.map((seg, i) => (
              <span
                key={i}
                onMouseEnter={() => setHoveredSegment(seg.id)}
                onMouseLeave={() => setHoveredSegment(null)}
                className={cn(
                  "relative inline rounded px-1 py-0.5 mx-0.5 transition-colors cursor-help border-b-2",
                  seg.color,
                  `border-${seg.border.split('-')[1]}`, // dynamic border color mapping trick or use style
                  hoveredSegment === seg.id ? seg.highlight : "",
                  hoveredSegment === seg.id ? "z-10 shadow-sm" : ""
                )}
                style={{ borderColor: 'transparent' }} // fallback
                title={`${seg.label}: ${seg.analysis.substring(0, 100)}...`}
              >
                {seg.text}
                {/* Inline Label Badge on Hover */}
                {hoveredSegment === seg.id && (
                   <span className="absolute -top-6 left-0 bg-black text-white text-[10px] px-2 py-0.5 rounded shadow-lg whitespace-nowrap z-20 pointer-events-none">
                     {seg.label}
                   </span>
                )}
                {" "}
              </span>
            ))}
          </div>
        </div>

        {/* Right Column: Analysis Boxes */}
        <div className="p-6 md:p-8 lg:col-span-5 bg-gray-50/50 space-y-4">
          <h5 className="font-bold text-gray-900 mb-6 flex items-center gap-2 uppercase tracking-wide text-sm">
            📊 Kontekst i Analiza
          </h5>
          {segments.map((seg, i) => (
            <div
              key={i}
              className={cn(
                "p-4 rounded-xl border-l-4 transition-all duration-300",
                seg.border,
                "bg-white shadow-sm",
                hoveredSegment === seg.id ? "ring-2 ring-offset-2 ring-indigo-200 scale-[1.02]" : "opacity-80 hover:opacity-100"
              )}
            >
              <div className={cn("text-xs font-bold uppercase tracking-widest mb-1", seg.text_color)}>
                {seg.label}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {seg.analysis}
              </p>
            </div>
          ))}
          
          {/* Viral Hook (Separate) */}
          <div className="p-4 rounded-xl border-l-4 border-purple-500 bg-purple-50 shadow-sm mt-6">
             <div className="text-xs font-bold uppercase tracking-widest mb-1 text-purple-900">Viral Hook</div>
             <p className="text-sm text-purple-800 italic">{reel.analysis_point_7}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export function Reels() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recapData, setRecapData] = useState(null);
  const [reelsData, setReelsData] = useState(null);
  const [copiedItems, setCopiedItems] = useState({});
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const fetchReelsData = async () => {
      try {
        const today = new Date().toISOString().split('T')[0]; 
        const statusResponse = await fetch(`${API_BASE}/reels-status.php?date=${today}`, {
          credentials: 'include'
        });

        if (statusResponse.status === 401) {
          navigate('/');
          return;
        }

        const statusResult = await statusResponse.json();

        if (!statusResult.success || !statusResult.ready) {
          toast.error('Scenariusze rolek nie są jeszcze gotowe dla dzisiejszej daty');
          setLoading(false);
          return;
        }

        const { reels_key, recap_key } = statusResult;
        localStorage.setItem('reels_keys', JSON.stringify({ reels_key, recap_key }));

        const [recapResponse, reelsResponse] = await Promise.all([
          fetch(`${API_BASE}/preview.php?key=${recap_key}`, { credentials: 'include' }),
          fetch(`${API_BASE}/preview.php?key=${reels_key}`, { credentials: 'include' })
        ]);

        const recapResult = await recapResponse.json();
        const reelsResult = await reelsResponse.json();

        if (recapResult.success && reelsResult.success) {
          setRecapData(recapResult.data);
          setReelsData(reelsResult.data);
          toast.success('Scenariusze rolek załadowane');
        } else {
          toast.error('Błąd ładowania scenariuszy');
        }
      } catch (error) {
        toast.error('Błąd pobierania scenariuszy rolek');
        console.error('Error fetching reels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReelsData();
  }, [navigate]);

  const copyToClipboard = async (text, itemId) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }

      setCopiedItems(prev => ({ ...prev, [itemId]: true }));
      toast.success('Skopiowano do schowka');
      setTimeout(() => {
        setCopiedItems(prev => ({ ...prev, [itemId]: false }));
      }, 2000);
    } catch (error) {
      toast.error('Błąd kopiowania');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-semibold text-muted-foreground">Ładowanie scenariuszy rolek...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
      {/* Header */}
      <header className="mb-6 flex justify-between items-center">
        <div>
          <button onClick={() => navigate('/')} className="text-sm text-primary hover:underline mb-2 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Powrót do Dashboard
          </button>
          <h2 className="text-3xl font-bold">🎬 Scenariusze Rolek</h2>
          <p className="text-muted-foreground mt-1">Wygenerowane scenariusze dla YouTube Shorts/TikTok i Recap</p>
        </div>
        <button 
          onClick={() => setShowDebug(!showDebug)} 
          className="px-3 py-1 bg-gray-200 text-xs rounded hover:bg-gray-300 transition-colors"
        >
          🐞 Debug
        </button>
      </header>

      {/* Debug Section */}
      {showDebug && (
        <section className="mb-12 p-4 bg-slate-100 rounded-lg border border-slate-300 overflow-x-auto">
          <h3 className="font-bold mb-2">Raw Data Debug:</h3>
          <pre className="text-xs font-mono whitespace-pre-wrap break-all">
            {JSON.stringify({
              reelsDataType: Array.isArray(reelsData) ? 'Array' : typeof reelsData,
              reelsDataLength: Array.isArray(reelsData) ? reelsData.length : 'N/A',
              reelsData: reelsData 
            }, null, 2)}
          </pre>
        </section>
      )}

      {/* RECAP Section */}
      {recapData && (
        <section className="mb-16">
          <div className="mb-6">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">📺 RECAP - Scenariusz Podsumowania</h3>
            <p className="text-sm text-muted-foreground mt-1">Viral Ranking & Script</p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Analysis Column */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
                  <h4 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                    🔥 Ranking Wiralowy
                  </h4>
                </div>
                <div className="p-6 space-y-4">
                  {recapData.analysis_viral_ranking && recapData.analysis_viral_ranking.split('\n').filter(line => line.trim()).map((line, i) => {
                    // Simple parsing logic
                    const scoreMatch = line.match(/Ocena: \*\*(.*?)\*\*/);
                    const emotionsMatch = line.match(/Emocje: \*\*(.*?)\*\*/);
                    const titleMatch = line.match(/\d+\.\s\*\*(.*?)\*\*/);
                    
                    // Clean description by removing matched parts (naive approach, but effective for display)
                    let description = line
                      .replace(/\d+\.\s\*\*(.*?)\*\*/, '')
                      .replace(/-?\s*Ocena: \*\*(.*?)\*\*/, '')
                      .replace(/\.?\s*Emocje: \*\*(.*?)\*\*/, '')
                      .replace(/^\s*-\s*/, '') // remove leading dash if any
                      .trim();

                    const score = scoreMatch ? scoreMatch[1] : 'N/A';
                    const scoreNum = parseInt(score.split('/')[0]);
                    const title = titleMatch ? titleMatch[1] : line.split('-')[0]; // Fallback title
                    const emotions = emotionsMatch ? emotionsMatch[1].split(',').map(e => e.trim()) : [];

                    // Determine color based on score
                    let scoreColor = 'bg-gray-100 text-gray-600 border-gray-200';
                    if (scoreNum >= 9) scoreColor = 'bg-red-100 text-red-700 border-red-200';
                    else if (scoreNum >= 7) scoreColor = 'bg-orange-100 text-orange-700 border-orange-200';
                    else if (scoreNum >= 5) scoreColor = 'bg-yellow-100 text-yellow-700 border-yellow-200';

                    return (
                      <div key={i} className="flex gap-4 items-start p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                        <div className={`flex-shrink-0 w-12 h-12 flex flex-col items-center justify-center rounded-lg border ${scoreColor}`}>
                          <span className="text-lg font-bold">{scoreNum}</span>
                          <span className="text-[10px] opacity-80">/10</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h5 className="font-bold text-gray-900 text-base">{title}</h5>
                            <div className="flex gap-1">
                              {emotions.map((emotion, eIdx) => (
                                <span key={eIdx} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] uppercase font-bold tracking-wider rounded-full">
                                  {emotion}
                                </span>
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 leading-snug">{description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
                  <h4 className="text-lg font-bold text-indigo-900 flex items-center gap-2">
                    🧠 Uzasadnienie Wyboru
                  </h4>
                </div>
                <div className="p-6 text-sm text-gray-700 leading-relaxed">
                  {recapData.analysis_selection_reasoning && recapData.analysis_selection_reasoning.split('\n').map((line, i) => (
                    <p key={i} className="mb-3 last:mb-0">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Script Column */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full">
              <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-100 flex justify-between items-center sticky top-0 z-10">
                <h4 className="text-lg font-bold text-emerald-900">📝 Scenariusz Video</h4>
                <button
                  onClick={() => {
                    const fullScript = [
                      recapData.script_segment_1_hook_0_08,
                      recapData.script_segment_2_value_stack_08_25,
                      recapData.script_segment_3_bridge_25_35,
                      recapData.script_segment_4_cta_35_50
                    ].join('\n\n');
                    copyToClipboard(fullScript, 'recap-full');
                  }}
                  className="px-3 py-1.5 bg-white border border-emerald-200 rounded-lg text-xs font-bold text-emerald-700 hover:bg-emerald-50 transition-colors uppercase tracking-wide flex items-center gap-2"
                >
                  {copiedItems['recap-full'] ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  Kopiuj całość
                </button>
              </div>
              
              <div className="p-6 space-y-6 flex-1 bg-gray-50/50">
                {[ 
                  { label: 'HOOK (0-8s)', content: recapData.script_segment_1_hook_0_08, color: 'border-l-4 border-red-500' },
                  { label: 'VALUE STACK (8-25s)', content: recapData.script_segment_2_value_stack_08_25, color: 'border-l-4 border-blue-500' },
                  { label: 'BRIDGE (25-35s)', content: recapData.script_segment_3_bridge_25_35, color: 'border-l-4 border-purple-500' },
                  { label: 'CTA (35-50s)', content: recapData.script_segment_4_cta_35_50, color: 'border-l-4 border-green-500' }
                ].map((segment, idx) => (
                  <div key={idx} className={`bg-white p-5 rounded-xl shadow-sm border border-gray-100 relative ${segment.color}`}>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{segment.label}</span>
                      <button
                        onClick={() => copyToClipboard(segment.content, `recap-seg-${idx}`)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Kopiuj fragment"
                      >
                        {copiedItems[`recap-seg-${idx}`] ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-base text-gray-800 whitespace-pre-wrap leading-relaxed font-medium">
                      {segment.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* REELS Section */}
      {(() => {
        let reelsArray = [];
        if (reelsData) {
            if (Array.isArray(reelsData)) {
                reelsArray = reelsData;
            } else if (Array.isArray(reelsData.stories)) {
                reelsArray = reelsData.stories;
            } else if (Array.isArray(reelsData.data)) {
                reelsArray = reelsData.data;
            } else if (Array.isArray(reelsData.output)) {
                reelsArray = reelsData.output;
            }
        }

        if (!reelsArray || reelsArray.length === 0) return null;

        return (
        <section>
          <div className="mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">🎥 REELS - Scenariusze Stories</h3>
            <p className="text-sm text-muted-foreground mt-1">Dostępne warianty ({reelsArray.length})</p>
          </div>

          <div>
            {reelsArray.map((item, index) => {
              const reel = item.output || item;
              const reelData = reel.script_title ? reel : (reel.output || null);
              
              if (!reelData || !reelData.script_title) return null;

              return (
                <ReelCard 
                  key={index} 
                  reel={reelData} 
                  index={index} 
                  copyToClipboard={copyToClipboard} 
                  copiedItems={copiedItems} 
                />
              );
            })}
          </div>
        </section>
        );
      })()}

      {!recapData && !reelsData && (
        <div className="text-center py-12">
          <p className="text-gray-500">Brak danych do wyświetlenia</p>
        </div>
      )}
    </div>
  );
}
